import {
  CalendarSettingsDto,
  CalendarSettingsPatchDto,
  DayOfWeek,
  QueryFactory,
} from '@lmt/shared-types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Field } from '../../common/Field';
import { DropDownInput } from '../../common/ui-kit/inputs/dropdown/DropDownInput';
import { getEnumValuesDict } from '../../patients/list/common/types';
import { getTimeZones, TimeZone } from '@vvo/tzdb';
import styles from './SettingsBlock.module.scss';
import { useEventCallback } from '@mui/material';
import { TenantSettingsKeys } from './types';
import { useRegisterUnsavedChanges } from './useRegisterUnsavedChanges';

export const CalendarSettings: FC = React.memo(function CalendarSettings() {
  const i18n = useTranslation();
  const i18nSettings = useScopedTranslation(
    'tenant_settings.calendar_settings',
  );
  const translatedWeekDays = useMemo(
    () => getEnumValuesDict(DayOfWeek, 'week_day'),
    [],
  );

  const { data: calendarConfigurations } =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationsQuery();

  const { data: calendarSettings, refetch: refetch } =
    QueryFactory.CalendarSettingsQuery.useGetQuery();

  const form = useAdvancedForm<CalendarSettingsDto>(
    useCallback(async (data: CalendarSettingsDto) => {
      const dto: CalendarSettingsPatchDto = {
        ...data,
      };
      await QueryFactory.CalendarSettingsClient.patch(dto);
      await refetch();
    }, []),
    calendarSettings,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );
  const { formState, reset, control } = form;

  const timezones = useMemo(() => {
    return getTimeZones();
  }, []);

  const options = useMemo(() => {
    if (!calendarConfigurations) {
      return [];
    }
    return calendarConfigurations
      .filter((configuration) => configuration.isShared)
      .map((configuration) => {
        const displayValue: string = i18n.t('names.' + configuration.name, {
          defaultValue: configuration.name,
        });
        return {
          ...configuration,
          displayValue: displayValue.toLowerCase(),
        };
      })
      .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
  }, [calendarConfigurations]);

  const onReset = useEventCallback(() => {
    reset(calendarSettings);
  });

  useEffect(() => {
    onReset();
  }, [calendarSettings]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.Calendar,
    form,
  );

  return (
    <form onSubmit={form.handleSubmitDefault}>
      <div
        className={styles.container}
        data-test-id="calendar-settings-container"
      >
        <div className={styles.header}>
          <div className={styles.title}>
            {i18nSettings.t('calendar_settings_title')}
          </div>
          <div className={styles.optionContainer}>
            <Button
              disabled={!isUnsavedChanges}
              title={i18nSettings.t('reset_button_title')}
              className={styles.option}
              color={ButtonColor.Secondary}
              onClick={onReset}
            />
            <SubmitButton
              disabled={!isUnsavedChanges}
              title={i18nSettings.t('submit_button_title')}
              submitting={formState.isSubmitting}
              className={styles.option}
            />
          </div>
        </div>
        <Field
          title={i18nSettings.t('sleep_lab_timezone')}
          className={styles.field}
        >
          <Controller
            name={'timeZoneIanaId'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                required={true}
                useIdFunctionAsValue={true}
                idFunction={'name'}
                options={timezones}
                onValueChanged={(value: TimeZone | null) =>
                  onChange(value?.name)
                }
                getOptionLabel="currentTimeFormat"
                value={value}
              />
            )}
          />
        </Field>
        <Field
          title={i18nSettings.t('first_day_of_week')}
          className={styles.field}
        >
          <Controller
            name={'firstDayOfWeek'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                required
                getOptionLabel="label"
                idFunction="value"
                options={translatedWeekDays}
                value={value}
                onValueChanged={(value) => onChange(value.value)}
              />
            )}
          />
        </Field>
        <Field
          title={i18nSettings.t('default_calendar_view')}
          className={styles.field}
          hint={i18nSettings.t('default_calendar_view_hint')}
        >
          <Controller
            name={'defaultCalendarView'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                idFunction={'id'}
                getOptionLabel={(item) =>
                  i18n.t(item.name, { defaultValue: item.name })
                }
                required
                options={options}
                value={value}
                onValueChanged={(value) => onChange(value.id)}
              />
            )}
          ></Controller>
        </Field>
      </div>
    </form>
  );
});
